/**
 * Hook para buscar KPIs/totalizadores de agendamento online
 * 
 * Chama a RPC get_online_scheduling_stats com filtros de período e tipo de evento.
 * 
 * RELEASE: 2026-02-15
 * VERSION: 1.0.0
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { format } from 'date-fns';
import type { PeriodFilter } from '@/hooks/useCrmOverviewFilters';

const logger = createLogger('OnlineSchedulingStats');

/** Tipagem do retorno da RPC */
export interface OnlineSchedulingStats {
  total_period: number;
  total_today: number;
  open_count: number;
  overdue_count: number;
  completed_count: number;
  cancelled_count: number;
}

/** Parâmetros do hook */
interface UseOnlineSchedulingStatsParams {
  period: PeriodFilter;
  eventTypeId?: string | null;
}

/**
 * Hook que busca totalizadores de agendamento online via RPC
 */
export function useOnlineSchedulingStats({ period, eventTypeId }: UseOnlineSchedulingStatsParams) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id;

  // Formata datas para envio à RPC
  const startDate = format(period.startDate, 'yyyy-MM-dd');
  const endDate = format(period.endDate, 'yyyy-MM-dd');

  return useQuery({
    queryKey: ['online-scheduling-stats', tenantId, startDate, endDate, eventTypeId],
    queryFn: async (): Promise<OnlineSchedulingStats> => {
      logger.info('Buscando stats de agendamento online', { startDate, endDate, eventTypeId });

      const { data, error } = await supabase.rpc('get_online_scheduling_stats', {
        p_start_date: startDate,
        p_end_date: endDate,
        p_event_type_id: eventTypeId || null,
      });

      if (error) {
        logger.error('Erro ao buscar stats', error);
        throw error;
      }

      // A RPC retorna array com 1 registro
      const row = Array.isArray(data) ? data[0] : data;

      return {
        total_period: Number(row?.total_period ?? 0),
        total_today: Number(row?.total_today ?? 0),
        open_count: Number(row?.open_count ?? 0),
        overdue_count: Number(row?.overdue_count ?? 0),
        completed_count: Number(row?.completed_count ?? 0),
        cancelled_count: Number(row?.cancelled_count ?? 0),
      };
    },
    enabled: !!tenantId,
  });
}
